// src/components/HeroSection.jsx
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";

// -----------------------------------------------------------------------------
// Enhanced Typewriter Component with smoother animations and proper completion
// -----------------------------------------------------------------------------
const Typewriter = ({ text, speed = 50, delay = 0, className = "" }) => {
  const [displayedText, setDisplayedText] = useState("");
  const [cursorVisible, setCursorVisible] = useState(true);

  // Smooth blinking cursor effect
  useEffect(() => {
    const blinkInterval = setInterval(() => {
      setCursorVisible((prev) => !prev);
    }, 530);
    return () => clearInterval(blinkInterval);
  }, []);

  useEffect(() => {
    let animationFrame;
    let startTime = null;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime - delay;
      // If we're still in the delay period, request the next frame.
      if (elapsed < 0) {
        animationFrame = requestAnimationFrame(animate);
        return;
      }
      const totalDuration = text.length * speed;
      // When time exceeds the total duration, set the full text.
      if (elapsed >= totalDuration) {
        setDisplayedText(text);
        return;
      }
      // Calculate the current index and update the text accordingly.
      const targetIndex = Math.floor(elapsed / speed);
      setDisplayedText(text.slice(0, targetIndex + 1));
      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [text, speed, delay]);

  return (
    <span className={className}>
      {displayedText}
      <span
        className="inline-block w-[2px] h-[1em] ml-1 -mb-[0.2em] bg-current transition-opacity duration-150"
        style={{ opacity: cursorVisible ? 1 : 0 }}
      />
    </span>
  );
};

// -----------------------------------------------------------------------------
// Enhanced Grid Background Component
// -----------------------------------------------------------------------------
const GridBackground = () => {
  const gridItems = Array.from({ length: 144 }, (_, i) => ({
    id: i,
    duration: 2 + Math.random() * 2,
    delay: Math.random() * 2,
  }));

  return (
    <div className="absolute inset-0 grid grid-cols-12 grid-rows-12 gap-4 opacity-10">
      {gridItems.map(({ id, duration, delay }) => (
        <motion.div
          key={id}
          className="border border-green-500/20"
          initial={{ opacity: 0.3 }}
          animate={{
            opacity: [0.3, 0.8, 0.3],
            scale: [1, 1.05, 1],
          }}
          transition={{
            duration,
            delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Main Hero Section Component
// -----------------------------------------------------------------------------
const HeroSection = ({ onInitialize }) => {

  return (
    <motion.section
      className="relative min-h-[90vh] flex items-center justify-center pt-8 pb-8 overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <GridBackground />

      <motion.div
        className="relative z-10 max-w-7xl mx-auto px-4 text-center"
        initial={{ y: 20 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <h1 className="text-4xl md:text-7xl font-bold mb-6 font-mono">
          <Typewriter
            text="AIOrbis Digital Agency"
            speed={40}
            className="inline-block text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500"
          />
          <br />
          <Typewriter
            text="AI-Powered Solutions for Your Growth"
            speed={40}
            delay={1200}
            className="inline-block"
          />
        </h1>

        <p className="text-lg md:text-xl text-green-300/80 mb-6 max-w-3xl mx-auto font-mono">
          <Typewriter
            text="We transform your ideas into dynamic digital solutions. With creativity, technology, and AI-driven insights, we deliver results that set your brand apart."
            speed={25}
            delay={2400}
          />
        </p>

        <motion.div
          className="flex justify-center gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 4 }}
        >
          <button
            className="px-8 py-3 bg-green-500 text-gray-900 font-mono relative group overflow-hidden rounded-md"
            onClick={onInitialize}
          >
            <span className="relative z-10 transition-colors duration-300">
              Initialize System
            </span>
            <motion.span
              className="absolute inset-0 bg-green-300"
              initial={{ x: "-100%" }}
              whileHover={{ x: 0 }}
              transition={{ type: "spring", stiffness: 100, damping: 20 }}
            />
          </button>
        </motion.div>
      </motion.div>
    </motion.section>
  );
};

export default HeroSection;
